/**
 * The idle world's advance in closed form and the player's actions (D240 to D244).
 *
 * Pure and deterministic: time is always passed in, in whole milliseconds. Progress is whole,
 * and coins are whole below 2^53, so there advancing in one step gives exactly what many small
 * steps give. Past 2^53 coins are plain doubles and the two agree within rounding (D256). The advance is split only where a multiplier changes (a timed bonus starting or
 * ending) and where the barn fills; within each piece every stat is constant.
 */
import type {
  IdleAlmanacBranchT,
  IdleChainT,
  IdleContent,
  IdlePerkT,
  IdleProducerT,
  IdleUpgradeT,
} from '@hedgerow/data';
import { IDLE_ALMANAC_BRANCHES } from '@hedgerow/data';
import {
  idleAllProducerStats,
  idleBarnCapacityMs,
  idleEffects,
  idleFactor,
  idleHandCost,
  idleProducerFor,
  idleProducerStats,
  idleUpgradeCost,
  type IdleEffect,
} from './effects.ts';
import { idleMissedVisitors, idleVisitorAt, idleVisitorCoins } from './bonuses.ts';
import { noteIdleCollection } from './collection.ts';
import { idleFillLineage } from './lineage.ts';
import { idleGiftDue, idleNoteMade, settleIdleDaily, type IdleGift } from './daily.ts';
import { idleStartingYear, idleTraitsBarnLevel } from './festival.ts';
import { idleGoalsNewlyMet, meetIdleGoals } from './goals.ts';
import { idleMissedFindSlots, idleWeatherDue } from './luck.ts';
import {
  idleBarnOverflow,
  idleBarnUpgradeCost,
  idleBuyCost,
  idleChain,
  idleClamp,
  idleProducer,
  idleUnlockPrice,
} from './rules.ts';
import type { IdleWorldState, IdleProducerState } from './state.ts';
import {
  idleForkClosedBy,
  idleMasteryLevel,
  idleMasteryTier,
  idleMasteryView,
  idleStepOpen,
  idleStepView,
  idleTakeFork,
} from './tree.ts';

export type IdleRefusal =
  | 'unknown_producer'
  | 'locked'
  | 'already_unlocked'
  | 'unlock_in_order'
  | 'not_enough_coins'
  | 'has_hand'
  | 'not_ready'
  | 'barn_top_level'
  | 'unknown_chain'
  | 'already_built'
  | 'unknown_upgrade'
  | 'already_bought'
  | 'festival_not_ready'
  | 'unknown_perk'
  | 'perk_top_level'
  | 'not_enough_heritage'
  | 'no_offer'
  | 'bad_count'
  | 'needs_festivals'
  /** D367: a perk that needs another perk owned first (a hand on a crop needs the crop). */
  | 'needs_perk'
  | 'pass_on_not_ready'
  | 'unknown_trait'
  | 'trait_top_level'
  | 'not_enough_lineage'
  /** D276: the festival has been held and the new year not started. */
  | 'festival_pending'
  /** D276: `startIdleYear` with no festival waiting. */
  | 'not_pending'
  /** D277: taps have taken all they may off this bar; the next bar can be tapped again. */
  | 'tap_spent'
  /** D291: no such decoration. */
  | 'unknown_decoration'
  /** D291: a decoration chosen that the world has not bought. */
  | 'not_owned'
  /** D331: not enough Trimmings; `needs` is the shortfall. */
  | 'not_enough_trimmings'
  /** D331: a seasonal decoration whose weekly event is not running. */
  | 'out_of_season'
  /** D304: no such order today, or it has been sent. */
  | 'no_order'
  /** D304: the order does not yet have all it asks for. */
  | 'order_not_ready'
  /** D301: the producer stands on a piece of land no trait of the world's has opened. */
  | 'land_not_open'
  /** D454: the step's tier is not open yet. */
  | 'tier_locked'
  /** D454: the other side of the step's fork was taken; it opens again at pass-on. */
  | 'fork_closed'
  /** D454 step 3: plots move only from the generation `arrange.moveFrom` names. */
  | 'too_early'
  /** D454 step 3: no plot of that producer's on the world to move, or to swap with. */
  | 'no_plot'
  /** D454 step 3: the spot is off the land, on the yard or a festival mark, or on another plot. */
  | 'bad_spot'
  /** D454 step 3: the plot moved is too big for the other plot's place. */
  | 'too_big'
  /** D454 step 3: the other plot is too big for the moved plot's place. */
  | 'other_too_big';

export type IdleResult =
  | { ok: true; state: IdleWorldState }
  /** `needs` is the shortfall: coins for `not_enough_coins` and `festival_not_ready`, Heritage for `not_enough_heritage`, Trimmings for `not_enough_trimmings`, festivals for a decoration's or a perk's `needs_festivals`. */
  | { ok: false; reason: IdleRefusal; needs?: number };

export interface IdleWelcomeBack {
  /** Time since the player was last here. */
  away: number;
  /** Coins earned in that time, up to the barn's capacity. */
  earned: number;
  /** True when the player was away longer than the barn holds. */
  capped: boolean;
  /** The barn's capacity, so the screen can say how much it holds. */
  capacity: number;
  /** D361: the share of income past `capacity` the barn kept, 0 when it keeps none. */
  overflow: number;
  /** True when good weather greets the player on this check-in (D244). */
  weather: boolean;
  /** Goals met while away, in content order. They are marked, and pay, on this check-in (D248). */
  goals: string[];
  /** Lucky finds missed while here, paid into the barn on this check-in (D254). Counted in `earned`. */
  missedFinds: number;
  /** Coins those missed finds pay. */
  missedCoins: number;
  /**
   * D313: visitors who called while here, were not met, and had gone by the time the player
   * was away, in the order they came. Their coins are already in `earned`, paid into the barn
   * as missed lucky finds are (D254).
   */
  missedVisitors: string[];
  /**
   * D276: the festival was held and its year left waiting while the player was away, so this
   * check-in starts it. The number of the year started, or absent.
   */
  yearStarted?: number;
  /** D304: the first visit of the day pays a gift on this check-in. Absent otherwise. */
  gift?: IdleGift;
}

/**
 * Bring every bar in line with the stats in force at `t`: when a bar's length has changed,
 * its progress is rescaled so it stays the same share of the bar. Run after anything that
 * can change a multiplier.
 */
export function normaliseIdle(
  state: IdleWorldState,
  content: IdleContent,
  t: number,
): IdleWorldState {
  const stats = idleAllProducerStats(state, content, idleEffects(state, content, t));
  let changed = false;
  const producers: Record<string, IdleProducerState> = {};
  for (const [id, ps] of Object.entries(state.year.producers)) {
    const { cycleMs } = stats[id]!;
    if (cycleMs === ps.cycleMs) {
      producers[id] = ps;
      continue;
    }
    changed = true;
    const progressMs = Math.min(cycleMs, Math.floor((ps.progressMs * cycleMs) / ps.cycleMs));
    const next: IdleProducerState = { ...ps, cycleMs, progressMs };
    if (ps.tapped) next.tapped = Math.floor((ps.tapped * cycleMs) / ps.cycleMs);
    producers[id] = next;
  }
  return changed ? { ...state, year: { ...state.year, producers } } : state;
}

/** Times in (from, to) where a multiplier changes. */
function breakpoints(state: IdleWorldState, from: number, to: number): number[] {
  const ts = new Set<number>();
  for (const b of state.bonuses)
    for (const t of [b.from, b.until]) if (t > from && t < to) ts.add(t);
  const pb = state.region;
  if (pb) for (const t of [pb.from, pb.until]) if (t > from && t < to) ts.add(t);
  return [...ts].sort((a, b) => a - b);
}

/**
 * Run every bar for `dt` with constant `effects`. `share` of what the bars pay and make is kept:
 * all of it, or the barn's overflow share once it is full (D361).
 */
function runFor(
  state: IdleWorldState,
  content: IdleContent,
  effects: IdleEffect[],
  dt: number,
  share = 1,
): IdleWorldState {
  let earned = 0;
  const producers: Record<string, IdleProducerState> = {};
  const made: Record<string, number> = {};
  const stats = idleAllProducerStats(state, content, effects);
  for (const [id, ps] of Object.entries(state.year.producers)) {
    if (!ps.running) {
      producers[id] = ps;
      continue;
    }
    const { cycleMs, payout } = stats[id]!;
    if (ps.hand) {
      const total = ps.progressMs + dt;
      const cycles = Math.floor(total / cycleMs);
      earned += cycles * payout;
      // D304: a bar makes one unit for each owned, towards the day's orders.
      made[id] = cycles * ps.owned;
      const next: IdleProducerState = { ...ps, progressMs: total - cycles * cycleMs };
      // D277: a bar that pays starts with nothing tapped off it.
      if (cycles > 0 && next.tapped !== undefined) delete next.tapped;
      producers[id] = next;
    } else {
      producers[id] = { ...ps, progressMs: Math.min(cycleMs, ps.progressMs + dt) };
    }
  }
  if (share < 1) {
    earned = Math.floor(earned * share);
    for (const id of Object.keys(made)) made[id] = Math.floor(made[id]! * share);
  }
  return idleNoteMade(earn({ ...state, year: { ...state.year, producers } }, earned), made);
}

function earn(state: IdleWorldState, paid: number): IdleWorldState {
  // D316: every sum is held below IDLE_MAX, so no figure can become Infinity or NaN.
  const coins = idleClamp(paid);
  if (coins === 0) return state;
  const y = state.year;
  return {
    ...state,
    year: { ...y, coins: idleClamp(y.coins + coins), earned: idleClamp(y.earned + coins) },
    permanent: {
      ...state.permanent,
      lifetimeEarned: idleClamp(state.permanent.lifetimeEarned + coins),
    },
  };
}

/**
 * Bring the world up to `now` in closed form. Earning stops once the barn is full: at
 * `lastSeen` plus the barn's capacity, or carries on at the barn's overflow share (D361).
 * Hand-run producers pay every full bar; a manual bar fills once and waits. Does not mark the
 * player as here (a tick worker may call it).
 */
export function advanceIdle(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleWorldState {
  if (now <= state.advancedTo) return state;
  // D276: between the festival and the new year nothing earns; the clock only moves on.
  if (state.festivalPending) return noteIdleCollection({ ...state, advancedTo: now }, content, now);
  const capacity = idleBarnCapacityMs(state, content, idleEffects(state, content, state.lastSeen));
  const stop = Math.min(now, state.lastSeen + capacity);
  // D361: past a full day, a barn level keeps a share of what is earned once it is full.
  const overflow = idleBarnOverflow(content, state.permanent.barnLevel);
  let s = state;
  let t = state.advancedTo;
  for (const next of [...breakpoints(state, t, now), now]) {
    s = normaliseIdle(s, content, t);
    const fx = idleEffects(s, content, t);
    const end = Math.min(next, stop);
    if (end > t) s = runFor(s, content, fx, end - t);
    const from = Math.max(t, stop);
    if (overflow > 0 && next > from) s = runFor(s, content, fx, next - from, overflow);
    t = next;
  }
  s = normaliseIdle(s, content, now);
  const bonuses = s.bonuses.filter((b) => b.until > now);
  const advanced = {
    ...s,
    advancedTo: now,
    bonuses: bonuses.length === s.bonuses.length ? s.bonuses : bonuses,
  };
  // D290: what the year has come to goes in the collection. Looks only; nothing earns from it.
  return noteIdleCollection(advanced, content, now);
}

/** Advance to `now` and mark the player as here. Every action does this first. */
export function checkInIdle(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleWorldState {
  if (state.festivalPending) return checkInPending(state, content, now);
  const advanced = advanceIdle(state, content, now);
  // D304: the gift is worked out as the welcome back works it out, from the world advanced.
  const gift = idleGiftDue(advanced, content, now);
  let s = meetIdleGoals(
    payMissedVisitors(payMissedFinds(advanced, content, now), content, now),
    content,
  );
  s = settleIdleDaily(s, content, now, gift);
  // Good weather may greet a player back from a long absence (D244). It starts now, so the
  // away time already counted is untouched and the closed form stays exact.
  if (idleWeatherDue(state, content, now)) {
    const w = content.bonuses.weather;
    const weather = { source: 'good_weather', stat: 'value' as const, factor: w.factor };
    s = { ...s, bonuses: [...s.bonuses, { ...weather, from: now, until: now + w.durationMs }] };
  }
  const here = {
    ...s,
    lastSeen: Math.max(s.lastSeen, now),
    earnedAtSeen: s.permanent.lifetimeEarned,
  };
  // D290: a visitor at the gate while the player is here has been seen.
  const noted = noteIdleCollection(here, content, now, idleVisitorAt(here, content, now)?.visitor);
  return s === state ? noted : normaliseIdle(noted, content, now);
}

/**
 * A check-in between the festival and the new year (D276). Nothing earns, no goal is met and no
 * bonus comes. A player back after `festival.startAfterAwayMs` finds the new year started for
 * them, with the perks they hold.
 */
function checkInPending(state: IdleWorldState, content: IdleContent, now: number): IdleWorldState {
  const s = advanceIdle(state, content, now);
  const here = (x: IdleWorldState): IdleWorldState => ({
    ...x,
    lastSeen: Math.max(x.lastSeen, now),
    earnedAtSeen: x.permanent.lifetimeEarned,
  });
  if (!idleYearStartsOnReturn(state, content, now)) return here(s);
  return here(idleBeginYear(s, content, now));
}

/** True when a check-in at `now` starts a waiting year: the player was away long enough (D276). */
export function idleYearStartsOnReturn(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): boolean {
  return !!state.festivalPending && now - state.lastSeen >= content.festival.startAfterAwayMs;
}

/** The year after a held festival, begun at `now` with the head start the perks give (D276). */
export function idleBeginYear(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleWorldState {
  const pending = state.festivalPending;
  if (!pending) return state;
  const { festivalPending: _done, ...held } = state;
  // D388: a generation begun after passing on starts at the barn level its traits give.
  const barnLevel = Math.max(held.permanent.barnLevel, idleTraitsBarnLevel(held, content));
  const rest = { ...held, permanent: { ...held.permanent, barnLevel } };
  const year = idleStartingYear(rest, content, pending.year + 1, now);
  return normaliseIdle({ ...rest, year }, content, now);
}

/**
 * What the player earned while away, for the welcome-back payout. Pass the state as stored
 * (advanced or not) and the time of return. Call before `checkInIdle`, which resets it.
 */
export function idleWelcomeBack(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleWelcomeBack {
  const after = advanceIdle(state, content, now);
  const capacity = idleBarnCapacityMs(state, content, idleEffects(state, content, state.lastSeen));
  const away = Math.max(0, now - state.lastSeen);
  const pending = !!state.festivalPending;
  const missed = pending ? [] : idleMissedFindSlots(after, content, now);
  const missedCoins = missed.length * idleLuckyFindCoins(after, content, now);
  const missedVisitors = pending ? [] : idleMissedVisitors(after, content, now);
  const missedVisitorCoins = missedVisitors.length * idleVisitorCoins(after, content, now);
  const started = idleYearStartsOnReturn(state, content, now);
  const gift = idleGiftDue(after, content, now);
  return {
    away,
    earned: after.permanent.lifetimeEarned - state.earnedAtSeen + missedCoins + missedVisitorCoins,
    capped: away > capacity,
    capacity,
    overflow: idleBarnOverflow(content, state.permanent.barnLevel),
    goals: pending ? [] : idleGoalsNewlyMet(after, meetIdleGoals(after, content)),
    weather: !pending && idleWeatherDue(state, content, now),
    missedFinds: missed.length,
    missedCoins,
    missedVisitors,
    ...(started ? { yearStarted: state.festivalPending!.year + 1 } : {}),
    ...(gift ? { gift } : {}),
  };
}

export interface IdleBarnWaste {
  /** Time the barn was full and nothing more could be earned. Zero when it never filled. */
  lostMs: number;
  /** Share of the time away lost to a full barn, 0 to 1. */
  lostShare: number;
  /** Coins a barn holding `atCapacityMs` would have kept instead of losing, capped by what was lost. */
  wouldHaveKept: number;
}

/**
 * What a full barn cost over `awayMs`, holding `capacityMs` and earning `rateCoinsPerMs` (D324).
 * Pass a bigger `atCapacityMs` (an upgrade's own capacity) to see what that level would have
 * kept instead; left at `capacityMs` it is zero. D361: `overflow` is the share the barn keeps past
 * its capacity, and `atOverflow` the share the upgrade would keep.
 */
export function idleBarnWaste(
  capacityMs: number,
  rateCoinsPerMs: number,
  awayMs: number,
  atCapacityMs: number = capacityMs,
  overflow = 0,
  atOverflow: number = overflow,
): IdleBarnWaste {
  const lostMs = Math.max(0, awayMs - capacityMs);
  const lostShare = awayMs > 0 ? lostMs / awayMs : 0;
  const kept = (cap: number, share: number) =>
    Math.min(awayMs, cap) + share * Math.max(0, awayMs - cap);
  const wouldHaveKept = Math.max(
    0,
    rateCoinsPerMs *
      (kept(Math.max(atCapacityMs, capacityMs), atOverflow) - kept(capacityMs, overflow)),
  );
  return { lostMs, lostShare, wouldHaveKept };
}

/** Coins a second the world makes at time `t`. Manual producers count only with `withManual`. */
export function idleIncome(
  state: IdleWorldState,
  content: IdleContent,
  t: number,
  withManual = false,
): number {
  if (state.festivalPending) return 0;
  const stats = idleAllProducerStats(state, content, idleEffects(state, content, t));
  let rate = 0;
  for (const [id, ps] of Object.entries(state.year.producers))
    if (ps.hand || withManual) rate += stats[id]!.rate;
  return rate;
}

/** A lucky find pays this many seconds of the world's income, tapped bars counted, times any find effect. */
export function idleLuckyFindCoins(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): number {
  const fx = idleEffects(state, content, now);
  const worth = content.bonuses.luckyFind.worthSeconds * idleFactor(fx, 'find');
  return Math.max(1, Math.floor(idleIncome(state, content, now, true) * worth));
}

/**
 * Lucky finds missed while the player was here pay into the barn (D254): counted as away
 * earnings, so the welcome back shows them. Paid on a check-in, never in a bare advance.
 */
function payMissedFinds(state: IdleWorldState, content: IdleContent, now: number): IdleWorldState {
  const missed = idleMissedFindSlots(state, content, now);
  if (missed.length === 0) return state;
  return earn(state, missed.length * idleLuckyFindCoins(state, content, now));
}

/**
 * Visitors missed while away pay into the barn too (D313), the same way missed lucky finds do
 * (D254). Paid on a check-in, never in a bare advance.
 */
function payMissedVisitors(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleWorldState {
  const missed = idleMissedVisitors(state, content, now);
  if (missed.length === 0) return state;
  return earn(state, missed.length * idleVisitorCoins(state, content, now));
}

/** D295: festivals a perk counts as, for opening producers, with `level` levels owned. */
export function idlePerkFestivals(perk: IdlePerkT, level: number): number {
  return (perk.festivals ?? 0) * level;
}

/** Festivals held in this generation (D257). */
export function idleFestivalsThisGeneration(state: IdleWorldState): number {
  return state.permanent.festivals.length - (state.permanent.lineage?.festivalsBefore ?? 0);
}

/** Festivals that count for opening producers: this generation's, and those perks add (D250, D251). */
export function idleFestivalsCounted(state: IdleWorldState, content: IdleContent): number {
  let n = idleFestivalsThisGeneration(state);
  for (const k of content.perks) n += idlePerkFestivals(k, state.permanent.perks[k.id] ?? 0);
  return n;
}

/** Festivals still to hold before this producer can be unlocked; 0 when it can (D251). */
export function idleFestivalsNeeded(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
): number {
  const need = idleProducer(content, id)?.needsFestivals ?? 0;
  return Math.max(0, need - idleFestivalsCounted(state, content));
}

/** True when a Lineage trait makes every producer come with its hand hired (D257). */
export function idleHandsHired(state: IdleWorldState, content: IdleContent): boolean {
  const traits = state.permanent.lineage?.traits ?? {};
  return content.lineage.traits.some((t) => t.hands && (traits[t.id] ?? 0) > 0);
}

/** True when the manual bar of this producer is full and a tap will harvest it. */
export function idleReady(state: IdleWorldState, id: string): boolean {
  const ps = state.year.producers[id];
  return !!ps && !ps.hand && ps.running && ps.progressMs >= ps.cycleMs;
}

type Owned = { p: IdleProducerT; ps: IdleProducerState };

function owned(state: IdleWorldState, content: IdleContent, id: string): Owned | IdleResult {
  const p = idleProducer(content, id);
  if (!p) return { ok: false, reason: 'unknown_producer' };
  const ps = state.year.producers[id];
  if (!ps) return { ok: false, reason: 'locked' };
  return { p, ps };
}

function short(s: IdleWorldState, cost: number): IdleResult | undefined {
  const coins = s.year.coins;
  return coins < cost ? { ok: false, reason: 'not_enough_coins', needs: cost - coins } : undefined;
}

/** Pay `cost`, set one producer, and bring the bars in line with any new multiplier. */
function done(
  s: IdleWorldState,
  content: IdleContent,
  now: number,
  cost: number,
  id?: string,
  ps?: IdleProducerState,
): IdleResult {
  const producers = id && ps ? { ...s.year.producers, [id]: ps } : s.year.producers;
  const next = { ...s, year: { ...s.year, coins: idleClamp(s.year.coins - cost), producers } };
  const met = noteIdleCollection(meetIdleGoals(next, content), content, now);
  return { ok: true, state: normaliseIdle(met, content, now) };
}

/** Refused while the festival waits for its new year (D276): the year's actions wait with it. */
function pendingRefusal(s: IdleWorldState): IdleResult | undefined {
  return s.festivalPending ? { ok: false, reason: 'festival_pending' } : undefined;
}

/** Coins paid while the player is here: `earnedAtSeen` moves too, so they never count as away. */
function earnHere(s: IdleWorldState, coins: number): IdleWorldState {
  const paid = earn(s, coins);
  return { ...paid, earnedAtSeen: paid.permanent.lifetimeEarned };
}

/**
 * Tap a producer. A manual one: harvest its full bar and start the next, or start an idle bar.
 * One run by a hand (D277): take a share of a bar off its current bar, paying it if that
 * finishes it. Taps take at most `content.tap.maxShare` of any one bar (`tap_spent` after).
 */
export function tapIdle(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const o = owned(s, content, id);
  if ('ok' in o) return o;
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  const { ps } = o;
  if (ps.hand) return tapHand(s, content, id, ps, now);
  if (!ps.running) return done(s, content, now, 0, id, { ...ps, running: true, progressMs: 0 });
  if (ps.progressMs < ps.cycleMs) return { ok: false, reason: 'not_ready' };
  const pay = idleProducerStats(s, content, id, idleEffects(s, content, now)).tapPayout;
  const paid = idleNoteMade(earnHere(s, pay), { [id]: ps.owned });
  return done(paid, content, now, 0, id, { ...ps, progressMs: 0 });
}

/** The share of a bar one tap takes off a hand-run producer's bar, after tap effects (D277). */
export function idleHandTapShare(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  effects: IdleEffect[],
): number {
  return Math.min(content.tap.maxShare, content.tap.share * idleFactor(effects, 'tap', id));
}

/** Time taps may still take off this producer's current bar (D277); 0 when spent or not hand-run. */
export function idleHandTapLeftMs(state: IdleWorldState, content: IdleContent, id: string): number {
  const ps = state.year.producers[id];
  if (!ps?.hand) return 0;
  const cap = Math.floor(content.tap.maxShare * ps.cycleMs);
  return Math.max(0, Math.min(cap - (ps.tapped ?? 0), ps.cycleMs - ps.progressMs));
}

function tapHand(
  s: IdleWorldState,
  content: IdleContent,
  id: string,
  ps: IdleProducerState,
  now: number,
): IdleResult {
  const fx = idleEffects(s, content, now);
  const want = Math.max(1, Math.round(idleHandTapShare(s, content, id, fx) * ps.cycleMs));
  const skip = Math.min(want, idleHandTapLeftMs(s, content, id));
  if (skip <= 0) return { ok: false, reason: 'tap_spent' };
  const progressMs = ps.progressMs + skip;
  if (progressMs < ps.cycleMs)
    return done(s, content, now, 0, id, { ...ps, progressMs, tapped: (ps.tapped ?? 0) + skip });
  // The tap finishes the bar: it pays now and the next bar starts untapped.
  const { tapped: _spent, ...rest } = ps;
  const pay = idleProducerStats(s, content, id, fx).payout;
  const paid = idleNoteMade(earnHere(s, pay), { [id]: ps.owned });
  return done(paid, content, now, 0, id, { ...rest, progressMs: 0 });
}

/** Buy `n` more of an unlocked producer. */
export function buyIdle(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  n: number,
  now: number,
): IdleResult {
  if (!Number.isInteger(n) || n < 1) return { ok: false, reason: 'bad_count' };
  const s = checkInIdle(state, content, now);
  const o = owned(s, content, id);
  if ('ok' in o) return o;
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  const cost = idleBuyCost(idleProducerFor(s, content, id) ?? o.p, o.ps.owned, n);
  return short(s, cost) ?? done(s, content, now, cost, id, { ...o.ps, owned: o.ps.owned + n });
}

/**
 * Hire the hand for a producer: from now on its bar runs and pays by itself. Hiring never strands
 * a bar (D272): a full bar is harvested as a tap would and the hand starts a fresh one, a bar part
 * done carries on where it was, and a bar not started starts now.
 */
export function hireIdleHand(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const o = owned(s, content, id);
  if ('ok' in o) return o;
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  const { ps } = o;
  if (ps.hand) return { ok: false, reason: 'has_hand' };
  const fx = idleEffects(s, content, now);
  const cost = idleHandCost(s, content, id, fx);
  const lacking = short(s, cost);
  if (lacking) return lacking;
  const full = ps.running && ps.progressMs >= ps.cycleMs;
  const progressMs = full || !ps.running ? 0 : Math.max(0, ps.progressMs);
  const hired: IdleProducerState = { ...ps, hand: true, running: true, progressMs };
  const paid = full ? earnHere(s, idleProducerStats(s, content, id, fx).tapPayout) : s;
  return done(paid, content, now, cost, id, hired);
}

/**
 * The producers shown to unlock (D261, D275): the next `IDLE_SHOWN` not yet unlocked, in content
 * order. Any of them can be unlocked, in any order, once affordable and its festivals held.
 */
export function idleShownProducers(state: IdleWorldState, content: IdleContent): IdleProducerT[] {
  const locked = (land?: string) =>
    content.producers
      .filter((p) => p.land === land && !state.year.producers[p.id])
      .slice(0, IDLE_SHOWN);
  // D301: each open land shows the next two of its own line after the world's.
  return [locked(), ...idleOpenLands(state, content).map((l) => locked(l))].flat();
}

/** D301: the pieces of land beside the world that its Lineage traits have opened, by id. */
export function idleOpenLands(state: IdleWorldState, content: IdleContent): string[] {
  const traits = state.permanent.lineage?.traits ?? {};
  const open = new Set<string>();
  for (const t of content.lineage.traits)
    if (t.opensLand !== undefined && (traits[t.id] ?? 0) > 0) open.add(t.opensLand);
  return content.map.lands.filter((l) => open.has(l.id)).map((l) => l.id);
}

/**
 * D352: coins to unlock a producer: its own price scaled by the festivals held this generation
 * (`prices.unlockPerFestival` each). Set when the year starts and fixed all year; income and
 * bonuses never move it. In the first year it is the producer's own price.
 */
export function idleUnlockCost(state: IdleWorldState, content: IdleContent, id: string): number {
  const p = idleProducer(content, id);
  if (!p) return 0;
  return idleClamp(
    Math.round(
      idleUnlockPrice(content, p, idleFestivalsThisGeneration(state)) *
        (state.year.priceFactor ?? 1),
    ),
  );
}

/** How many locked producers are shown at once (D261, D275). */
export const IDLE_SHOWN = 2;

/** Unlock a shown producer (D275). It comes with one owned and an idle bar. */
export function unlockIdleProducer(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const p = idleProducer(content, id);
  if (!p) return { ok: false, reason: 'unknown_producer' };
  if (s.year.producers[id]) return { ok: false, reason: 'already_unlocked' };
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  if (p.land !== undefined && !idleOpenLands(s, content).includes(p.land))
    return { ok: false, reason: 'land_not_open' };
  // Not yet shown: further down the list than the next two (D275).
  if (!idleShownProducers(s, content).includes(p)) return { ok: false, reason: 'unlock_in_order' };
  if (idleFestivalsNeeded(s, content, id) > 0) return { ok: false, reason: 'needs_festivals' };
  const hand = idleHandsHired(s, content);
  const fresh = { owned: 1, hand, running: hand, progressMs: 0, cycleMs: p.cycleMs };
  // D352: priced from the world's stage at the start of the year, never from income.
  const cost = idleUnlockCost(s, content, id);
  return short(s, cost) ?? done(s, content, now, cost, id, fresh);
}

/** Raise the barn one level, so it holds more hours of away time. */
export function upgradeIdleBarn(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  const level = s.permanent.barnLevel;
  const cost = idleBarnUpgradeCost(content, level);
  if (cost === undefined) return { ok: false, reason: 'barn_top_level' };
  const raised = { ...s, permanent: { ...s.permanent, barnLevel: level + 1 } };
  return short(s, cost) ?? done(raised, content, now, cost);
}

/** True once a chain's festival perk, if it needs one, is owned (D281: shared with the screen). */
export function idleChainPerkMet(state: IdleWorldState, ch: IdleChainT): boolean {
  return ch.needsPerk === undefined || !!state.permanent.perks[ch.needsPerk];
}

/**
 * What still holds a chain back, or undefined when it can be built once its producer is owned
 * (D281, D343: shared with the screen). `perk` is the festival perk it needs, `festivals` how many
 * more festivals must be counted, `chain` the building on the same crop that must stand first.
 */
export function idleChainLock(
  state: IdleWorldState,
  content: IdleContent,
  ch: IdleChainT,
): { perk?: string; festivals?: number; chain?: string } | undefined {
  if (!idleChainPerkMet(state, ch)) return { perk: ch.needsPerk! };
  const festivals = Math.max(0, (ch.needsFestivals ?? 0) - idleFestivalsCounted(state, content));
  if (festivals > 0) return { festivals };
  if (ch.needsChain !== undefined && !state.year.chains.includes(ch.needsChain))
    return { chain: ch.needsChain };
  return undefined;
}

/** Build a chain building (the mill, the dairy): it lifts its producer's value for the year. */
export function buildIdleChain(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  const ch = idleChain(content, id);
  if (!ch) return { ok: false, reason: 'unknown_chain' };
  if (s.year.chains.includes(id)) return { ok: false, reason: 'already_built' };
  if (!s.year.producers[ch.producer]) return { ok: false, reason: 'locked' };
  if (idleChainLock(s, content, ch)) return { ok: false, reason: 'locked' };
  // D325: a chain built adds a little to the Lineage meter.
  const built = idleFillLineage(
    { ...s, year: { ...s.year, chains: [...s.year.chains, id] } },
    content,
    'chain',
  );
  return short(s, ch.cost) ?? done(built, content, now, ch.cost);
}

/**
 * The Almanac steps that can be bought now, coins apart (D454): not bought this year, their tier
 * open, what they need in place (their producer owned, their page's perk, D250; festivals; the
 * year's hours; a count owned), and their fork's other side not taken this generation (D252).
 */
export function idleUpgradesVisible(state: IdleWorldState, content: IdleContent): IdleUpgradeT[] {
  return content.upgrades.filter((u) => idleStepOpen(state, content, u));
}

/** True when the other side of this step's fork closes it (D252, D454). */
export function idleChoiceClosed(
  state: IdleWorldState,
  content: IdleContent,
  u: IdleUpgradeT,
): boolean {
  return idleForkClosedBy(state, content, u) !== undefined;
}

/** The upgrades of a choice (D252), in content order. */
export function idleChoiceUpgrades(content: IdleContent, choice: string): IdleUpgradeT[] {
  return content.upgrades.filter((u) => u.choice === choice);
}

/** Buy an Almanac upgrade on show. It lasts for the year. */
export function buyIdleUpgrade(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  const u = content.upgrades.find((x) => x.id === id);
  if (!u) return { ok: false, reason: 'unknown_upgrade' };
  if (s.year.upgrades.includes(id)) return { ok: false, reason: 'already_bought' };
  const step = idleStepView(s, content, u);
  if (step.state === 'closed') return { ok: false, reason: 'fork_closed' };
  if (step.state !== 'open')
    return {
      ok: false,
      reason: step.needs?.tier || step.needs?.generation ? 'tier_locked' : 'locked',
    };
  // D454: a fork side bought closes the other until the world is passed on.
  const bought = idleTakeFork({ ...s, year: { ...s.year, upgrades: [...s.year.upgrades, id] } }, u);
  const cost = idleUpgradeCost(s, content, id, idleEffects(s, content, now));
  return short(s, cost) ?? done(bought, content, now, cost);
}

/**
 * D457: coins for a branch's next mastery level: `tree.mastery.first` x `growth` ^ level times
 * the dearest step of the generation's last tier, at today's price (its festival growth and
 * every upgrade-cost effect). A new generation's tier moves the price up with the tier; the
 * levels themselves reset with the year.
 */
export function idleMasteryCost(
  state: IdleWorldState,
  content: IdleContent,
  branch: IdleAlmanacBranchT,
  effects: IdleEffect[],
): number {
  const steps = idleMasteryTier(state, content, branch);
  const dearest = Math.max(0, ...steps.map((u) => idleUpgradeCost(state, content, u.id, effects)));
  const m = content.tree.mastery;
  return idleClamp(Math.round(dearest * m.first * m.growth ** idleMasteryLevel(state, branch)));
}

/** D457: buy a branch's next mastery level. It lasts for the year. */
export function buyIdleMastery(
  state: IdleWorldState,
  content: IdleContent,
  branch: string,
  now: number,
): IdleResult {
  const s = checkInIdle(state, content, now);
  const waiting = pendingRefusal(s);
  if (waiting) return waiting;
  if (!(IDLE_ALMANAC_BRANCHES as readonly string[]).includes(branch))
    return { ok: false, reason: 'unknown_upgrade' };
  const b = branch as IdleAlmanacBranchT;
  if (!idleMasteryView(s, content, b).open) return { ok: false, reason: 'tier_locked' };
  const cost = idleMasteryCost(s, content, b, idleEffects(s, content, now));
  const level = idleMasteryLevel(s, b) + 1;
  const bought = { ...s, year: { ...s.year, mastery: { ...s.year.mastery, [b]: level } } };
  return short(s, cost) ?? done(bought, content, now, cost);
}
